<template>
    <VJoyDialog
        ref="dialog"
        data-testid="authentication-token-popup"
        :confirm-text="$t('authentication.token.popup.submit.action')"
        :cancel-text="$t('authentication.token.popup.cancel.action')"
        @dialog:confirm="onConfirm"
        @dialog:hide="onHide"
    >
        <template #dialog-header>
            {{ $t('authentication.token.popup.title') }}
        </template>

        <template #dialog-body>
            <p v-if="verificationMethod === 'EMAIL'" data-testid="authentication-token-prompt">
                {{ $t('authentication.token.popup.body.email.method.prompt') }}
            </p>
            <p v-if="operationToken === 'account.verifyNewEmail'" data-testid="authentication-token-prompt">
                {{ $t('authentication.token.popup.body.verify.new.email.method.prompt') }}
            </p>
            <MaltOtpInput
                v-model="r$.$value.token"
                name="authentication-token-input"
                :error-message="errorMessage"
                data-testid="authentication-token-input"
            />
            <p data-testid="authentication-token-expiration-note">
                {{ $t('authentication.token.popup.body.expiration.note', [formattedExpirationTime]) }}
            </p>
            <pre v-if="developerHelp">{{ developerHelp }}</pre>
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {AuthenticationTokenApi, type AuthenticationTokenResponse, Configuration, FetchError} from '@authentication-token-api';
    import {exactDigits, required} from '@regle/rules';
    import {MaltOtpInput} from '#components';
    import {useTranslation} from '#imports';
    import {useRegle, validateForm} from '@malt/forms';
    import {type Deferred, deferred} from '@malt/utils';
    import {VJoyDialog} from '@maltjoy/core-vue';

    defineExpose({
        requestVerificationToken,
    });

    const {t} = useTranslation();

    const api = new AuthenticationTokenApi(new Configuration({basePath: ''}));

    const timeFormat = new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: 'numeric',
    });

    const developerHelp = ref('');
    const dialog = ref<InstanceType<typeof VJoyDialog>>();
    const verificationMethod = ref<string>();
    const operationToken = ref<string>();
    const formattedExpirationTime = ref<string>();

    const {r$} = useRegle(
        {token: ''},
        {
            token: {required, exactDigits: exactDigits(6)},
        },
        {lazy: true},
    );

    const errorMessage = computed(() => (r$.token.$invalid ? t('authentication.token.popup.error.invalid.token') : ''));

    export type Result = {
        canceled: boolean;
        tokenEnteredByUser?: string;
    };

    let deferredUserInput: Deferred<Result>;

    async function onConfirm() {
        const {valid, data} = await validateForm(r$);
        if (!valid) {
            return;
        }

        deferredUserInput.resolve({
            canceled: false,
            tokenEnteredByUser: data.token,
        });

        await dialog.value?.hide();
        onClose();
    }

    function onClose() {
        r$.$reset({toOriginalState: true});
    }

    function onHide() {
        deferredUserInput.resolve({
            canceled: true,
        });
        onClose();
    }

    async function requestVerificationToken(operation: string, email?: string): Promise<Result> {
        deferredUserInput = deferred();
        operationToken.value = operation;

        let tokenResponse: AuthenticationTokenResponse;
        try {
            tokenResponse = await api.requestAuthenticationToken({
                authenticationTokenRequest: {operation, email},
            });
        } catch (e: unknown) {
            if (!(e instanceof FetchError)) {
                throw e;
            }
            if (e.statusCode === 501) {
                // feature is disabled
                return Promise.resolve({
                    canceled: false,
                    tokenEnteredByUser: '424242', // irrelevant
                });
            } else if (e.statusCode === 403) {
                // code was not generated, most probably the account does not exist
                return Promise.resolve({
                    canceled: false,
                    tokenEnteredByUser: '424242', // irrelevant
                });
            } else if (e.statusCode === 502 && (window.location.hostname.match(/^dev[^.]*\.malt\./) || window.location.hostname === 'localhost')) {
                developerHelp.value =
                    'It looks like registration-backend is not responding,\n' +
                    'falling back to a mocked behavior.\n' +
                    'Use the following tokens to test authentication:\n' +
                    '\t222222: already consumed\n' +
                    '\t111111: expired\n' +
                    '\t123456: valid token\n' +
                    '\t260213: valid token too :-P\n' +
                    'Any other token will be considered invalid.';

                tokenResponse = {
                    verificationMethod: 'EMAIL',
                    expirationDateTime: new Date(Date.now() + 5 * 60 * 1000),
                };
            } else {
                throw e;
            }
        }

        if (tokenResponse.verificationMethod !== 'EMAIL') {
            throw new Error(`Unsupported verification method: ${tokenResponse.verificationMethod}`);
        }

        verificationMethod.value = tokenResponse.verificationMethod;
        formattedExpirationTime.value = timeFormat.format(tokenResponse.expirationDateTime);

        await dialog.value?.show();

        return deferredUserInput.promise;
    }
</script>
